import {
  apiGetGiftList,
  apiGetGiftListPackage,
  apiGiftSend,
  apiGiftSendMulti
} from '@/api/gift'
import type {
  giftInterface,
  giftSendRequestInterface,
  giftSendResponseInterface,
  giftSendMultiRequestInterface
} from '@/types/apiType/giftType'

export const useGiftStore = defineStore({
  id: 'gift',
  state: () => ({
    giftVersion: 0 as number, // 礼物接口版本
    giftList: [] as giftInterface[], // 礼物列表
    giftTypeList: [
      {
        type: 1,
        label: 'gift.Package',
        gifts: [] as giftInterface[][]
      },
      {
        type: 2,
        label: 'gift.General',
        gifts: [] as giftInterface[][]
      },
      {
        type: 6,
        label: 'gift.Celebrity',
        gifts: [] as giftInterface[][]
      },
      {
        type: 3,
        label: 'gift.Lucky',
        gifts: [] as giftInterface[][]
      },
      {
        type: 12,
        label: 'gift.CP',
        gifts: [] as giftInterface[][]
      },
      {
        type: 11,
        label: 'gift.Country',
        gifts: [] as giftInterface[][]
      },
      {
        type: 4,
        label: 'gift.Aristocracy',
        gifts: [] as giftInterface[][]
      },
      {
        type: 5,
        label: 'gift.Friendship',
        gifts: [] as giftInterface[][]
      }
    ], // 礼物类型
    giftListAnimation: [] as giftSendResponseInterface[], // 侧边礼物列表
    isShowBatter: false as boolean, // 是否显示连击按钮
    effectGifts: [] as giftSendResponseInterface[], // 动画礼物，播放特效的轮播
    micGifts: [] as giftSendResponseInterface[] // 麦位礼物
  }),
  actions: {
    /**
     * 移除动画特效子集
     * @param {shift} type '移除类型：shift：第一项'
     * */
    removeEffectGifts(params: { type: 'shift' }) {
      if (params.type === 'shift') {
        this.effectGifts.shift()
      }
    },
    /**
     * 设置显示连击按钮
     */
    setIsShowBatter(value: boolean) {
      this.isShowBatter = value
    },
    // 移除侧边礼物列表某一项
    removeGiftListAnimation(id: string) {
      const roomStore = useRoomStore()
      const index = this.giftListAnimation.findIndex((v) => v.id === id)
      if (index !== -1) {
        // 如果是幸运连击礼物
        if (this.giftListAnimation[index].comboRangEnd && roomStore.roomId) {
          // 礼物动画结束了，显示公屏
          roomStore.updateChatList('put', {
            id: new Date().getTime(),
            type: constant.ROOM_CHAT_TYPE.CHAT_GIFT,
            content: '',
            nick: this.giftListAnimation[index].nick, // 送礼人
            targetNick: this.giftListAnimation[index].targetNick, // 收礼人
            targetUid: this.giftListAnimation[index].targetUid || 0, // 收礼人id
            targetAvatar: this.giftListAnimation[index].targetAvatar, // 收礼人头像
            targetUids: this.giftListAnimation[index].targetUids, // 多人收礼人

            giftId: this.giftListAnimation[index].giftId, // 礼物id
            giftName: this.giftListAnimation[index].giftName, // 礼物每次
            giftUrl: this.giftListAnimation[index].giftUrl, // 礼物图片
            giftNum: this.giftListAnimation[index].comboRangEnd
          })
        }
        this.giftListAnimation.splice(index, 1)
      }
    },
    // 移除麦位礼物列表某一项
    removeMicGifts(micId: string) {
      const index = this.micGifts.findIndex((v) => v.micId === micId)
      if (index !== -1) {
        this.micGifts.splice(index, 1)
      }
    },
    // 送礼礼socket监听
    onGiftSend(data: giftSendResponseInterface) {
      const roomStore = useRoomStore()
      const publicStore = usePublicStore()
      const userStore = useUserStore()
      // 进房情况下才添加
      if (roomStore.roomId) {
        // 唯一ID `${data.comboId}${data.giftId}${data.uid}${data.targetUids?.join('')}`
        data.id = `${data.comboId}${data.giftId}${data.uid}${data.targetUids?.join('')}`
        data.micId = `${data.comboId}${new Date().getTime()}`
        const index = this.giftListAnimation.findIndex((v) => v.id === data.id)

        // 侧边栏礼物
        if (index === -1) {
          this.giftListAnimation.push(data)
        } else {
          this.giftListAnimation.splice(index, 1, data)
        }

        // 特效礼物，有值才播放，标签页面显示
        if (
          (data.mp4Url || data.vggUrl) &&
          publicStore.isPageVisible &&
          !userStore.userInfo.banRoomGiftAnimation
        ) {
          this.effectGifts.push(data)
        }

        //  麦位礼物 1 是开启，并且礼物总价大于520
        if (
          !userStore.userInfo.banRoomGiftAnimation &&
          (!roomStore.socketRoomInfo.giftEffectSwitch ||
            (data.targetUids &&
              data.goldPrice * data.giftNum * data.targetUids.length > 520))
        ) {
          this.micGifts.push(data)
        }
      }
    },
    /**
     * 礼物赠送
     * @param {number} type '赠送礼物类型：1，赠送给房主；2，私聊发礼物；3，房间内赠送给个人；6，动态送礼 7，主播送礼'
     * @param {number} targetUid '收礼用户ID'
     * @param {number} giftId '礼物ID'
     * @param {number} giftNum '礼物数量'
     * */
    async giftSend(
      params: {
        type: 1 | 2 | 3
        targetUid: number
        giftId: number
        giftNum: number
      },
      giftInfo: giftInterface
    ) {
      const roomStore = useRoomStore()
      const userStore = useUserStore()
      const data = {
        appVersion: '',
        comboCount: 0,
        comboFrequencyCount: 0,
        comboId: 0,
        os: 'web',
        expressMessage: '',
        giftId: params.giftId,
        giftNum: params.giftNum,
        momentId: 0,
        roomType: 0,
        roomId: 0,
        roomUid: 0,
        targetUid: params.targetUid,
        type: params.type
      } as giftSendRequestInterface
      // 送给房主或房间某个人
      if (params.type === 1 || params.type === 3) {
        data.roomType = roomStore.roomInfo.type
        data.roomId = roomStore.roomId
        data.roomUid = roomStore.roomInfo.uid
      }
      // 送给房主
      if (params.type === 1) {
        data.targetUid = roomStore.roomInfo.uid
      }
      if (!data.targetUid || !data.giftId || !data.giftNum) return
      const res = await apiGiftSend(data)
      const { code, data: giftData } = res
      if (code === 200) {
        userStore.requestUserInfo()
        if (giftInfo.giftType === 6) {
          this.getGiftListPackage()
        }
        return Promise.resolve({ ...giftData, giftInfo })
      }
      // 显示余额不足弹窗
      if (code === 10049) {
        const publicStore = usePublicStore()
        publicStore.setShowRechargePopup(true)
        res.message && showToast(res.message)
      } else {
        res.message && showToast(res.message)
      }

      return
    },
    /**
     * 多人礼物赠送
     * @param {string} targetUids '多个收礼用户ID'
     * @param {number} giftId '礼物ID'
     * @param {number} giftNum '礼物数量'
     * */
    async giftSendMulti(
      params: {
        targetUids: string
        giftId: number
        giftNum: number
      },
      giftInfo: giftInterface
    ) {
      const roomStore = useRoomStore()
      const userStore = useUserStore()
      const data = {
        appVersion: '',
        comboCount: 0,
        comboFrequencyCount: 0,
        comboId: 0,
        os: 'web',
        giftId: params.giftId,
        giftNum: params.giftNum,
        momentId: 0,
        roomType: 0,
        roomId: 0,
        roomUid: 0,
        targetUids: params.targetUids
      } as giftSendMultiRequestInterface
      data.roomType = roomStore.roomInfo.type
      data.roomId = roomStore.roomId
      data.roomUid = roomStore.roomInfo.uid
      if (!data.targetUids || !data.giftId || !data.giftNum) return
      const res = await apiGiftSendMulti(data)
      const { code, data: giftData } = res
      if (code === 200) {
        userStore.getLevelExperienceInfo()
        if (giftInfo.giftType === 6) {
          this.getGiftListPackage()
        }
        return Promise.resolve(giftData)
      }
      // 显示余额不足弹窗
      if (code === 10049) {
        const publicStore = usePublicStore()
        publicStore.setShowRechargePopup(true)
        res.message && showToast(res.message)
      } else {
        res.message && showToast(res.message)
      }

      return
    },
    // 获取礼物列表,用户礼物列表
    getGiftList() {
      apiGetGiftList({
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line no-undef
        appVersion: '',
        giftScreen: 0,
        giftVersion: this.giftVersion
      }).then((res) => {
        const { code, data } = res
        if (code === 200 && data) {
          this.giftVersion = Number(data.version)
          if (data.gift) {
            // 过滤
            const temporaryGiftTypeInfo = {
              1: [],
              2: [],
              6: [],
              3: [],
              12: [],
              11: [],
              4: [],
              5: []
            } as { [prop: string]: giftInterface[] }

            this.giftList = data.gift
            //分类
            data.gift.forEach((v) => {
              // 背包礼物和贵族礼物需要特殊判断
              if (temporaryGiftTypeInfo[v.giftType]) {
                switch (v.giftType) {
                  case 1:
                    if (v.userGiftPurseNum > 0) {
                      temporaryGiftTypeInfo[v.giftType].push(v)
                    }
                    break
                  case 4:
                    if (v.nobleId > 0 && !v.isNobleGift) {
                      temporaryGiftTypeInfo[v.giftType].push(v)
                    }
                    break
                  default:
                    temporaryGiftTypeInfo[v.giftType].push(v)
                    break
                }
              }
            })
            // 转二维赋值
            this.giftTypeList.forEach((v) => {
              if (temporaryGiftTypeInfo[v.type]) {
                v.gifts = common.arrayToMatrix(
                  temporaryGiftTypeInfo[v.type],
                  8
                ) as giftInterface[][]
              }
            })
          }
        }
      })
    },
    //获取背包礼物列表
    getGiftListPackage() {
      apiGetGiftListPackage({
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line no-undef
        appVersion: '',
        app: 'web',
        giftVersion: 0,
        os: 'web'
      }).then((res) => {
        const { code, data } = res
        if (code === 200) {
          this.giftTypeList[0].gifts = common.arrayToMatrix(
            data.gift,
            8
          ) as giftInterface[][]
        }
      })
    },
    /**
     * 重置房间使用到的数据
     */
    resetRoomData() {
      this.giftListAnimation = []
      this.effectGifts = []
      this.micGifts = []
    },
    // 重置数据
    resetData() {
      this.giftList = []
      this.giftVersion = 0
      this.giftTypeList = [
        {
          type: 1,
          label: 'gift.Package',
          gifts: [] as giftInterface[][]
        },
        {
          type: 2,
          label: 'gift.General',
          gifts: [] as giftInterface[][]
        },
        {
          type: 6,
          label: 'gift.Celebrity',
          gifts: [] as giftInterface[][]
        },
        {
          type: 3,
          label: 'gift.Lucky',
          gifts: [] as giftInterface[][]
        },
        {
          type: 12,
          label: 'gift.CP',
          gifts: [] as giftInterface[][]
        },
        {
          type: 11,
          label: 'gift.Country',
          gifts: [] as giftInterface[][]
        },
        {
          type: 4,
          label: 'gift.Aristocracy',
          gifts: [] as giftInterface[][]
        },
        {
          type: 5,
          label: 'gift.Friendship',
          gifts: [] as giftInterface[][]
        }
      ]
      this.resetRoomData()
    }
  },
  persist: {
    key: 'giftStore',
    paths: ['giftList', 'giftVersion', 'giftTypeList']
  }
})
